/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import styled from "@emotion/styled";
import { colors } from "@farmlands/mui-theme";
import { Box, FormControlLabel, IconButton, Radio, RadioGroup } from "@mui/material";
import React from "react";
import { createSearchParams, useNavigate } from "react-router-dom";

import MaterialSymbol from "../../../components/MaterialSymbol";
import TextField from "../../../components/TextField";
import { usePermission } from "../../../hooks/usePermission";
import Analytics from "../../../services/analytics";
import TestIds from "../../../testIds";
import { PARAM_KEYS, PRODUCTS_ROUTES } from "../../products/constants";

const Container = styled("form")`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;

  background: linear-gradient(90deg, #004733 -1.7%, #00553d 33.83%, #6f8a5a 145.38%);
`;

const SearchBar = () => {
  const canViewPreviousOrders = usePermission("FarmlandsProShop_PreviousOrdersView").allowed;
  const [searchText, setSearchText] = React.useState<string>("");
  const [selected, setSelected] = React.useState("0");
  const navigate = useNavigate();
  const search = (event?: React.SyntheticEvent) => {
    event?.preventDefault();

    if (searchText) {
      Analytics.push({
        dataLayer: {
          event: "search_term",
          searchTerm: searchText,
        },
      });
    }

    navigate({
      pathname: PRODUCTS_ROUTES.BASE,
      search: createSearchParams({
        [PARAM_KEYS.search]: searchText ?? "",
        [PARAM_KEYS.tab]: selected,
      }).toString(),
    });
  };

  return (
    <Container onSubmit={search}>
      <Box width="100%">
        <TextField
          sx={{ height: "45px", pr: "3px" }}
          variant={"outlined"}
          placeholder="Search Farmlands products"
          value={searchText}
          testId={TestIds.components.searchWidget.searchBar}
          inputProps={{ maxLength: 200 }}
          InputProps={{
            endAdornment: (
              <IconButton
                onClick={search}
                color="primary"
                title="Search"
                data-testid={TestIds.components.searchWidget.searchIcon}
              >
                <MaterialSymbol>search</MaterialSymbol>
              </IconButton>
            ),
          }}
          onChange={(event) => setSearchText(event.target.value)}
        />
      </Box>

      <Box display="flex" width="100%" justifyContent={"space-between"}>
        <RadioGroup
          aria-labelledby="demo-radio-buttons-group-label"
          value={selected}
          name="search-radio-buttons-group"
          color="tertiary"
          sx={{ display: "flex", flexDirection: "row", color: "white" }}
          onChange={(_, value) => setSelected(value)}
          data-testid={TestIds.components.searchWidget.radioButtonGroup}
        >
          <FormControlLabel
            slotProps={{
              typography: {
                color: colors.shade0,
              },
            }}
            control={<Radio color="tertiary" />}
            value="0"
            label="All"
            data-id={TestIds.components.searchWidget.radioButtonGroupDataId("all")}
            data-testid={TestIds.components.searchWidget.radioButtonGroupDataId("all")}
          />
          {canViewPreviousOrders && (
            <FormControlLabel
              slotProps={{
                typography: {
                  color: colors.shade0,
                },
              }}
              control={<Radio color="tertiary" />}
              value="1"
              label="Previously ordered"
              data-id={TestIds.components.searchWidget.radioButtonGroupDataId("previously-ordered")}
              data-testid={TestIds.components.searchWidget.radioButtonGroupDataId(
                "previously-ordered"
              )}
            />
          )}
        </RadioGroup>
      </Box>
    </Container>
  );
};

export default SearchBar;
